import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }
import { getToken, refreshToken, getUserInfo } from '@edition/session'
import { createEditionApi, handleEditionError } from '@edition/api'
import type { CheckInCadence, ConfidenceLevel, CreatedScimToken, ExternalGroup, GroupAccessMapping, IdentityConfig, IdentityConfigUpdate, LearningEvent, MembershipEntityType, MembershipRole, ObjectiveStatus, OutcomeAssessment, PlatformIdentity, PlatformRoleChange, PaginatedResponse, ScimToken, VerifiedDomain } from '../types'

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    const tenantId = getUserInfo()?.tenantId
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId
    }
    return config
  },
  (error) => Promise.reject(error)
)

type ForbiddenNotifier = (path: string) => void
let forbiddenNotifier: ForbiddenNotifier | null = null

export function registerForbiddenNotifier(fn: ForbiddenNotifier | null) {
  forbiddenNotifier = fn
}


type UserProfileUpdateRequest = {
  firstName?: string | null
  lastName?: string | null
  position?: string | null
}

type UserNotificationPreferencesRequest = {
  weeklyCheckIns?: boolean
  mentions?: boolean
  adminSecurity?: boolean
}

type UserPreferencesRequest = {
  language?: 'en' | 'nb'
  colorTheme?: 'mono-flat' | 'mono' | 'color' | 'color-flat' | 'techy' | 'obsidian' | 'folio-bloom' | 'folio-oak'
  themeMode?: 'light' | 'dark' | 'system'
  monoAccent?: string
  monoBgDark?: string
  monoBgLight?: string
  sankeyStyle?: 'patterns' | 'colors'
  statusLanguage?: 'en' | 'nb'
  notifications?: UserNotificationPreferencesRequest
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined
    handleEditionError(error)
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      const refreshed = await refreshToken()
      const token = getToken()
      if (refreshed && token) {
        originalRequest.headers.Authorization = `Bearer ${token}`
        return apiClient(originalRequest)
      }
      if (window.location.pathname !== '/login') window.location.href = '/login'
    }
    if (error.response?.status === 403 && forbiddenNotifier) {
      forbiddenNotifier(originalRequest?.url ?? 'this resource')
    }
    return Promise.reject(error)
  }
)

/**
 * A money figure's span: one period by id, as every caller asked before, or a
 * window of periods (`window=YEAR`/`YTD` in a calendar year).
 */
export type PeriodOrWindow = string | { window: 'YEAR' | 'YTD'; year: number; asOf?: string }

export function windowParams(periodOrWindow?: PeriodOrWindow) {
  if (periodOrWindow == null) return {}
  return typeof periodOrWindow === 'string' ? { periodId: periodOrWindow } : periodOrWindow
}

const editionApi = createEditionApi(apiClient)

export const api = {
  ...editionApi.base,
  operatingModel: {
    /** The tenant's ladder: its levels, its goal rungs, and what it calls them. */
    get: (organizationId: string, locale?: string) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/operating-model`, {
        params: locale ? { locale } : undefined,
      }),
    templates: (locale?: string) =>
      apiClient.get('/api/v1/operating-model/templates', { params: locale ? { locale } : undefined }),
    /**
     * Units at one level, or at all of them. Replaces three endpoints that each
     * knew one level by name and could not see any other.
     */
    units: (
      organizationId: string,
      params?: { levelCode?: string; parentId?: string; locale?: string; includeArchived?: boolean },
    ) => apiClient.get(`/api/v1/organizations/${organizationId}/units`, { params }),
    /** Create a unit on any level, including one the three typed endpoints have no name for. */
    createUnit: (
      organizationId: string,
      body: { levelCode: string; parentId?: string | null; name: string; description?: string | null },
    ) => apiClient.post(`/api/v1/organizations/${organizationId}/units`, body),
    updateUnit: (
      organizationId: string,
      unitId: string,
      body: { name: string; description?: string | null; missionStatement?: string | null },
    ) => apiClient.patch(`/api/v1/organizations/${organizationId}/units/${unitId}`, body),
    /** Only a unit with nothing recorded against it; one with history answers 409 `unit_has_history`. */
    deleteUnit: (organizationId: string, unitId: string) =>
      apiClient.delete(`/api/v1/organizations/${organizationId}/units/${unitId}`),
    /** Stops a unit taking new work and keeps its history; later periods leave it out. */
    deactivateUnit: (organizationId: string, unitId: string) =>
      apiClient.post(`/api/v1/organizations/${organizationId}/units/${unitId}/deactivate`),
    reactivateUnit: (organizationId: string, unitId: string) =>
      apiClient.post(`/api/v1/organizations/${organizationId}/units/${unitId}/reactivate`),
    /** Place a unit under another, e.g. a team into a product area. The parent must sit on a higher level. */
    moveUnit: (organizationId: string, unitId: string, parentId: string) =>
      apiClient.put(`/api/v1/organizations/${organizationId}/units/${unitId}/parent`, { parentId }),
    /** Replaces the whole ladder: reordering is the common edit, so partial updates would need an ordering protocol. */
    update: (organizationId: string, body: unknown) =>
      apiClient.put(`/api/v1/organizations/${organizationId}/operating-model`, body),
  },

  organizations: {
    list: (params?: { page?: number; size?: number }) =>
      apiClient.get('/api/v1/organizations', { params }),
    get: (id: string) => apiClient.get(`/api/v1/organizations/${id}`),
    create: (data: { name: string; description?: string; missionStatement?: string; logoUrl?: string; visionStatement?: string }) =>
      apiClient.post('/api/v1/organizations', data),
    update: (id: string, data: { name?: string; description?: string; missionStatement?: string; logoUrl?: string; visionStatement?: string }) =>
      apiClient.put(`/api/v1/organizations/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/v1/organizations/${id}`),
    setLeader: (id: string, data: { leaderUserId: string | null; leaderTitle?: string }) =>
      apiClient.patch(`/api/v1/organizations/${id}/leader`, data),
  },

  divisions: {
    list: (organizationId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/divisions`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/divisions/${id}`),
    getDetails: (id: string) => apiClient.get(`/api/v1/divisions/${id}/details`),
    getDefault: (organizationId: string) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/default-division`),
    create: (organizationId: string, data: { name: string; description?: string; missionStatement?: string; parentId?: string }) =>
      apiClient.post(`/api/v1/organizations/${organizationId}/divisions`, data),
    update: (id: string, data: { name?: string; description?: string; missionStatement?: string; parentId?: string | null }) =>
      apiClient.put(`/api/v1/divisions/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/v1/divisions/${id}`),
    setLeader: (id: string, data: { leaderUserId: string | null; leaderTitle?: string }) =>
      apiClient.patch(`/api/v1/divisions/${id}/leader`, data),
  },

  valueStreams: {
    list: (divisionId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/divisions/${divisionId}/value-streams`, { params }),
    listByOrganization: (organizationId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/value-streams`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/value-streams/${id}`),
    getDetails: (id: string) => apiClient.get(`/api/v1/value-streams/${id}/details`),
    getDefault: (organizationId: string, divisionId: string) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/divisions/${divisionId}/default-value-stream`),
    create: (divisionId: string, data: { name: string; description?: string; missionStatement?: string }) =>
      apiClient.post(`/api/v1/divisions/${divisionId}/value-streams`, data),
    update: (id: string, data: { name?: string; description?: string; missionStatement?: string }) =>
      apiClient.put(`/api/v1/value-streams/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/v1/value-streams/${id}`),
    setLeader: (id: string, data: { leaderUserId: string | null; leaderTitle?: string }) =>
      apiClient.patch(`/api/v1/value-streams/${id}/leader`, data),
  },

  productTeams: {
    list: (organizationId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/product-teams`, { params }),
    listByValueStream: (valueStreamId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/value-streams/${valueStreamId}/product-teams`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/product-teams/${id}`),
    create: (valueStreamId: string, data: { name: string; description?: string; missionStatement?: string }) =>
      apiClient.post(`/api/v1/value-streams/${valueStreamId}/product-teams`, data),
    update: (id: string, data: { name?: string; description?: string; missionStatement?: string; valueStreamId?: string; checkInCadence?: CheckInCadence }) =>
      apiClient.put(`/api/v1/product-teams/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/v1/product-teams/${id}`),
    setLeader: (id: string, data: { leaderUserId: string | null; leaderTitle?: string }) =>
      apiClient.patch(`/api/v1/product-teams/${id}/leader`, data),
    roadmap: {
      list: (teamId: string) =>
        apiClient.get(`/api/v1/product-teams/${teamId}/roadmap-stories`),
      get: (teamId: string, storyId: string) =>
        apiClient.get(`/api/v1/product-teams/${teamId}/roadmap-stories/${storyId}`),
      create: (teamId: string, data: import('../types').RoadmapStoryRequest) =>
        apiClient.post(`/api/v1/product-teams/${teamId}/roadmap-stories`, data),
      update: (teamId: string, storyId: string, data: import('../types').RoadmapStoryRequest) =>
        apiClient.put(`/api/v1/product-teams/${teamId}/roadmap-stories/${storyId}`, data),
      reorder: (teamId: string, status: import('../types').RoadmapStoryStatus, orderedStoryIds: string[]) =>
        apiClient.put(`/api/v1/product-teams/${teamId}/roadmap-stories/order`, { status, orderedStoryIds }),
      delete: (teamId: string, storyId: string) =>
        apiClient.delete(`/api/v1/product-teams/${teamId}/roadmap-stories/${storyId}`),
    },
  },

  businessOpportunities: {
    list: (valueStreamId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/value-streams/${valueStreamId}/business-opportunities`, { params }),
    listByOrganization: (organizationId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/business-opportunities`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/business-opportunities/${id}`),
    create: (valueStreamId: string, data: {
      title: string; description?: string; priority?: string;
      expectedImpact?: string; successCriteria?: string;
      strategicIntentId?: string; hypothesis?: string; expectedOutcome?: string;
    }) => apiClient.post(`/api/v1/value-streams/${valueStreamId}/business-opportunities`, data),
    update: (id: string, data: {
      title?: string; description?: string; priority?: string;
      expectedImpact?: string; successCriteria?: string;
      strategicIntentId?: string; hypothesis?: string; expectedOutcome?: string;
    }) => apiClient.put(`/api/v1/business-opportunities/${id}`, data),
    evaluate: (id: string) => apiClient.post(`/api/v1/business-opportunities/${id}/evaluate`),
    approve: (id: string) => apiClient.post(`/api/v1/business-opportunities/${id}/approve`),
    start: (id: string) => apiClient.post(`/api/v1/business-opportunities/${id}/start`),
    /** Completing an initiative demands a verdict on its outcome (F-32). */
    complete: (id: string, data: { outcomeAssessment: OutcomeAssessment; outcomeNotes?: string }) =>
      apiClient.post(`/api/v1/business-opportunities/${id}/complete`, data),
    cancel: (id: string) => apiClient.post(`/api/v1/business-opportunities/${id}/cancel`),
    delete: (id: string) => apiClient.delete(`/api/v1/business-opportunities/${id}`),
  },

  strategicThemes: {
    list: (organizationId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/strategic-themes`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/strategic-themes/${id}`),
    create: (organizationId: string, data: { name: string; description?: string }) =>
      apiClient.post(`/api/v1/organizations/${organizationId}/strategic-themes`, data),
    update: (id: string, data: { name?: string; description?: string }) =>
      apiClient.put(`/api/v1/strategic-themes/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/v1/strategic-themes/${id}`),
  },

  strategicIntents: {
    list: (divisionId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/divisions/${divisionId}/strategic-intents`, { params }),
    listByOrganization: (organizationId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/strategic-intents`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/strategic-intents/${id}`),
    create: (divisionId: string, data: { name: string; description?: string; strategicThemeId?: string }) =>
      apiClient.post(`/api/v1/divisions/${divisionId}/strategic-intents`, data),
    update: (id: string, data: { name?: string; description?: string; strategicThemeId?: string }) =>
      apiClient.put(`/api/v1/strategic-intents/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/v1/strategic-intents/${id}`),
  },

  outcomeHypotheses: {
    list: (valueStreamId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/value-streams/${valueStreamId}/outcome-hypotheses`, { params }),
    listByOrganization: (organizationId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/organizations/${organizationId}/outcome-hypotheses`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/outcome-hypotheses/${id}`),
    create: (valueStreamId: string, data: { name: string; hypothesis: string; expectedOutcome?: string }) =>
      apiClient.post(`/api/v1/value-streams/${valueStreamId}/outcome-hypotheses`, data),
    update: (id: string, data: { name?: string; hypothesis?: string; expectedOutcome?: string }) =>
      apiClient.put(`/api/v1/outcome-hypotheses/${id}`, data),
    /** Record a verdict with the evidence behind it (F-22); evidence is optional. */
    resolve: (id: string, verdict: 'validate' | 'invalidate', data: { evidenceNote?: string }) =>
      apiClient.post(`/api/v1/outcome-hypotheses/${id}/${verdict}`, data),
    reopen: (id: string) => apiClient.post(`/api/v1/outcome-hypotheses/${id}/reopen`),
    delete: (id: string) => apiClient.delete(`/api/v1/outcome-hypotheses/${id}`),
  },

  learning: {
    /** Decisions and hypothesis resolutions for one entity, newest first (F-22). */
    timeline: (ownerType: string, ownerId: string) =>
      apiClient.get<LearningEvent[]>('/api/v1/learning-timeline', { params: { ownerType, ownerId } }),
  },

  periods: {
    list: (params?: { page?: number; size?: number }) =>
      apiClient.get('/api/v1/periods', { params }),
    getCurrent: () => apiClient.get('/api/v1/periods/current'),
    get: (id: string) => apiClient.get(`/api/v1/periods/${id}`),
    create: (data: { name: string; type: string; startDate: string; endDate: string }) =>
      apiClient.post('/api/v1/periods', data),
    generate: (year: number) =>
      apiClient.post('/api/v1/periods/generate', { year }),
    activate: (id: string) =>
      apiClient.post(`/api/v1/periods/${id}/activate`),
    close: (id: string) =>
      apiClient.post(`/api/v1/periods/${id}/close`),
    scoreObjectives: (periodId: string, scores: { objectiveId: string; finalScore: number; reflection?: string }[]) =>
      apiClient.post(`/api/v1/periods/${periodId}/objectives/score`, { scores }),
    carryForward: (periodId: string, targetPeriodId: string, objectiveIds: string[]) =>
      apiClient.post(`/api/v1/periods/${periodId}/carry-forward`, { targetPeriodId, objectiveIds }),
  },

  objectives: {
    list: (params: { periodId: string; organizationId: string; page?: number; size?: number; includeKeyResults?: boolean }) =>
      apiClient.get('/api/v1/objectives', { params }),
    listBusinessOpportunityLinkedByOrganization: (
      organizationId: string,
      params?: { periodId?: string; page?: number; size?: number },
    ) => apiClient.get(`/api/v1/organizations/${organizationId}/objectives/linked-to-business-opportunities`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/objectives/${id}`),
    getDetails: (id: string) => apiClient.get(`/api/v1/objectives/${id}/details`),
    create: (data: {
      title: string
      description?: string
      periodId: string
      ownerType: string
      ownerId: string
      parentId?: string
      businessOpportunityId?: string
      parentGoalId?: string
      outcomeHypothesisId?: string
      /** A shared objective's goal type on the owner's level. */
      rungId?: string
    }) => apiClient.post('/api/v1/objectives', data),
    update: (id: string, data: {
      title?: string
      description?: string
      weight?: number
      confidence?: ConfidenceLevel
      parentId?: string | null
      startDate?: string | null
      endDate?: string | null
      displayOrder?: number
      businessOpportunityId?: string | null
      parentGoalId?: string | null
      outcomeHypothesisId?: string | null
      /** Moved with the fields in one transaction; a refused move is a 409 and saves nothing. */
      status?: ObjectiveStatus
    }) => apiClient.put(`/api/v1/objectives/${id}`, data),
    /** The work testing one outcome hypothesis (F-34). */
    byOutcomeHypothesis: (outcomeHypothesisId: string) =>
      apiClient.get('/api/v1/objectives/by-outcome-hypothesis', { params: { outcomeHypothesisId } }),
    delete: (id: string) => apiClient.delete(`/api/v1/objectives/${id}`),
    activate: (id: string) => apiClient.post(`/api/v1/objectives/${id}/activate`),
    complete: (id: string) => apiClient.post(`/api/v1/objectives/${id}/complete`),
    cancel: (id: string) => apiClient.post(`/api/v1/objectives/${id}/cancel`),
    /** Each status this objective could move to, with `blockedReason` (`period_closed`, `work_started`, `approval_required`) or null. */
    statusOptions: (id: string) =>
      apiClient.get<Array<{ status: ObjectiveStatus; blockedReason: string | null }>>(`/api/v1/objectives/${id}/status-options`),
    listByOwner: (params: { ownerType: string; ownerId: string; periodId?: string; size?: number; includeKeyResults?: boolean }) =>
      apiClient.get('/api/v1/objectives/by-owner', { params }),
    /** A unit's own objectives on any level, without naming its owner type. Needs a period. */
    listByUnit: (params: { unitId: string; periodId: string; size?: number; includeKeyResults?: boolean }) =>
      apiClient.get('/api/v1/objectives/by-unit', { params }),
  },

  keyResults: {
    list: (objectiveId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/objectives/${objectiveId}/key-results`, { params }),
    get: (id: string) => apiClient.get(`/api/v1/key-results/${id}`),
    create: (
      objectiveId: string,
      data: {
        title: string
        description?: string
        metricType: string
        startValue?: number
        targetValue: number
        unit?: string
      }
    ) => apiClient.post(`/api/v1/objectives/${objectiveId}/key-results`, data),
    update: (id: string, data: {
      title?: string; description?: string; targetValue?: number;
      startValue?: number; unit?: string; weight?: number; metricType?: string;
      notes?: string; displayOrder?: number;
    }) => apiClient.put(`/api/v1/key-results/${id}`, data),
    updateProgress: (id: string, data: { value: number; note?: string }) =>
      apiClient.post(`/api/v1/key-results/${id}/progress`, data),
    progressHistory: (id: string) =>
      apiClient.get(`/api/v1/key-results/${id}/progress`),
    delete: (id: string) => apiClient.delete(`/api/v1/key-results/${id}`),
  },


  dependencies: {
    listByEntity: (params: { entityType: string; entityId: string }) =>
      apiClient.get('/api/v1/dependencies/by-entity', { params }),
    listByObjectiveIds: (objectiveIds: string[]) =>
      apiClient.get('/api/v1/dependencies/by-objectives', { params: { objectiveIds } }),
    get: (id: string) => apiClient.get(`/api/v1/dependencies/${id}`),
    create: (data: {
      sourceType: string; sourceId: string;
      targetType: string; targetId: string;
      dependencyType?: string; kind?: string; description?: string;
      ownerId?: string;
    }) => apiClient.post('/api/v1/dependencies', data),
    // Request that one or more of my objectives depend on another TEAM.
    // The receiving team approves/declines the bundle once.
    request: (data: {
      organizationId: string
      sourceObjectiveIds: string[]
      sourceObjectiveNames?: string[]
      requestingTeamId?: string
      requestingTeamName?: string
      targetTeamId: string
      targetTeamName?: string
      kind: string
      description: string
      referencedEntities?: { type: string; id: string; name: string }[]
    }) => apiClient.post('/api/v1/dependencies/requests', data),
    updateStatus: (id: string, data: { status: string }) =>
      apiClient.put(`/api/v1/dependencies/${id}/status`, data),
    delete: (id: string) => apiClient.delete(`/api/v1/dependencies/${id}`),
  },

  comments: {
    list: (params: { entityType: string; entityId: string }) =>
      apiClient.get('/api/v1/comments', { params }),
    create: (data: { entityType: string; entityId: string; content: string }) =>
      apiClient.post('/api/v1/comments', data),
    delete: (id: string) => apiClient.delete(`/api/v1/comments/${id}`),
  },

  users: {
    ...editionApi.users,
    me: () => apiClient.get('/api/v1/users/me'),
    /**
     * Where the signed-in user sits, for every membership they hold: the whole
     * chain from the root down, in one request. The client used to walk this
     * itself, one request per level, each waiting on the last.
     */
    context: (locale?: string) =>
      apiClient.get('/api/v1/users/me/context', { params: locale ? { locale } : undefined }),
    list: (params?: { page?: number; size?: number; activeOnly?: boolean }) =>
      apiClient.get('/api/v1/users', { params }),
    listByEntity: (params: { entityType: string; entityId: string; page?: number; size?: number }) =>
      apiClient.get('/api/v1/users/by-entity', { params }),
    listEntityMembers: (params: { entityType: string; entityId: string }) =>
      apiClient.get('/api/v1/users/entity-members', { params }),
    /** How many members a unit has; open to anyone who may read its goals. */
    countEntityMembers: (params: { entityType: string; entityId: string }) =>
      apiClient.get<{ count: number }>('/api/v1/users/entity-member-count', { params }),
    /** The people a member picker may offer; open to anyone who can add members. */
    colleagues: (params?: { page?: number; size?: number }) =>
      apiClient.get('/api/v1/users/colleagues', { params }),
    get: (id: string) => apiClient.get(`/api/v1/users/${id}`),
    getDetails: (id: string) => apiClient.get(`/api/v1/users/${id}/details`),
    create: (data: { keycloakId: string; email: string; firstName?: string; lastName?: string }) =>
      apiClient.post('/api/v1/users', data),
    update: (id: string, data: { email?: string; firstName?: string; lastName?: string; position?: string }) =>
      apiClient.put(`/api/v1/users/${id}`, data),
    updateProfile: (data: UserProfileUpdateRequest) =>
      apiClient.patch('/api/v1/users/me/profile', data),
    activate: (id: string) => apiClient.post(`/api/v1/users/${id}/activate`),
    deactivate: (id: string) => apiClient.post(`/api/v1/users/${id}/deactivate`),
    delete: (id: string) => apiClient.delete(`/api/v1/users/${id}`),
    updatePreferences: (preferences: UserPreferencesRequest) =>
      apiClient.patch('/api/v1/users/me/preferences', preferences),
    updateUserPreferences: (id: string, preferences: Record<string, unknown>) =>
      apiClient.put(`/api/v1/users/${id}/preferences`, preferences),
  },

  identity: {
    getConfig: () => apiClient.get<IdentityConfig>('/api/v1/identity/config'),
    updateConfig: (data: IdentityConfigUpdate) =>
      apiClient.put('/api/v1/identity/config', data),
    listDomains: () => apiClient.get<VerifiedDomain[]>('/api/v1/identity/domains'),
    createDomain: (data: { domain: string }) =>
      apiClient.post<VerifiedDomain>('/api/v1/identity/domains', data),
    verifyDomain: (id: string, data: { token: string }) =>
      apiClient.post<VerifiedDomain>(`/api/v1/identity/domains/${id}/verify`, data),
    listScimTokens: () => apiClient.get<ScimToken[]>('/api/v1/identity/scim-tokens'),
    createScimToken: (data: { name: string; expiresAt?: string }) =>
      apiClient.post<CreatedScimToken>('/api/v1/identity/scim-tokens', data),
    revokeScimToken: (id: string) =>
      apiClient.post<ScimToken>(`/api/v1/identity/scim-tokens/${id}/revoke`),
    listExternalGroups: () => apiClient.get<ExternalGroup[]>('/api/v1/identity/external-groups'),
    listGroupMappings: () => apiClient.get<GroupAccessMapping[]>('/api/v1/identity/group-mappings'),
    createGroupMapping: (data: { externalGroupId: string; entityType: MembershipEntityType; entityId: string; role: MembershipRole }) =>
      apiClient.post<GroupAccessMapping>('/api/v1/identity/group-mappings', data),
    deleteGroupMapping: (id: string) =>
      apiClient.delete(`/api/v1/identity/group-mappings/${id}`),
  },

  platformUsers: {
    search: (params?: { query?: string; page?: number; size?: number }) =>
      apiClient.get<PaginatedResponse<PlatformIdentity>>('/api/v1/platform/users', { params }),
    grantSuperAdmin: (subject: string) =>
      apiClient.put<PlatformRoleChange>(`/api/v1/platform/users/${encodeURIComponent(subject)}/super-admin`),
    removeSuperAdmin: (subject: string) =>
      apiClient.delete<PlatformRoleChange>(`/api/v1/platform/users/${encodeURIComponent(subject)}/super-admin`),
  },

  invitations: {
    list: () => apiClient.get('/api/v1/invitations'),
    create: (data: { email: string; firstName?: string; lastName?: string; entityType: string; entityId: string; role: string; expiresAt?: string }) =>
      apiClient.post('/api/v1/invitations', data),
    revoke: (id: string) => apiClient.post(`/api/v1/invitations/${id}/revoke`),
    sendEmail: (id: string) => apiClient.post(`/api/v1/invitations/${id}/send-email`),
    /** Signed out: whom an invitation is for, so the accept form can prefill the names. */
    preview: (data: { token: string }) =>
      apiClient.post<{ email: string; firstName?: string | null; lastName?: string | null }>('/api/v1/invitations/preview', data),
    accept: (data: { token: string; password?: string; firstName?: string; lastName?: string }) =>
      apiClient.post('/api/v1/invitations/accept', data),
  },

  memberships: {
    list: (userId: string) =>
      apiClient.get(`/api/v1/users/${userId}/memberships`),
    create: (userId: string, data: { entityType: string; entityId: string; role: string }) =>
      apiClient.post(`/api/v1/users/${userId}/memberships`, data),
    update: (userId: string, membershipId: string, data: { role?: string }) =>
      apiClient.put(`/api/v1/users/${userId}/memberships/${membershipId}`, data),
    delete: (userId: string, membershipId: string) =>
      apiClient.delete(`/api/v1/users/${userId}/memberships/${membershipId}`),
  },

  capacity: {
    /** A team's headcount for a period; 204 while nobody has entered one. */
    getTeam: (teamId: string, periodId?: string) =>
      apiClient.get<import('../types').TeamCapacity>(`/api/v1/capacity/teams/${teamId}`, { params: { periodId } }),
    setTeam: (teamId: string, data: {
      periodId?: string; fteCount: number; consultantFteCount: number; notes?: string;
    }) => apiClient.put(`/api/v1/capacity/teams/${teamId}`, data),
    /** Any unit's roll-up in FTE: one period, or -- given a window -- its periods averaged. */
    getUnit: (unitId: string, periodOrWindow?: PeriodOrWindow) =>
      apiClient.get<import('../types').UnitCapacity>(`/api/v1/capacity/units/${unitId}`, { params: windowParams(periodOrWindow) }),
  },

  analytics: {
    getBOCapacity: (boId: string, periodId?: string) =>
      apiClient.get<import('../types').BOCapacity>(`/api/v1/analytics/business-opportunities/${boId}/capacity`, { params: { periodId } }),
    getStrategicInitiatives: (orgId: string, periodId?: string) =>
      apiClient.get(`/api/v1/analytics/organizations/${orgId}/strategic-initiatives`, { params: { periodId } }),
    getTeamHealth: (teamId: string, periodId?: string) =>
      apiClient.get(`/api/v1/analytics/teams/${teamId}/health`, { params: { periodId } }),
    getOrganizationHealthMatrix: (orgId: string, periodId?: string) =>
      apiClient.get(`/api/v1/analytics/organizations/${orgId}/health-matrix`, { params: { periodId } }),
    getSettledOutcomes: (orgId: string) =>
      apiClient.get<import('../types').SettledOutcomes>(`/api/v1/analytics/organizations/${orgId}/settled-outcomes`),
  },
  approvals: {
    requestMembership: (data: { teamId: string; teamName: string; organizationId: string }) =>
      apiClient.post('/api/v1/approvals/membership', data),
    requestOkrApproval: (data: { objectiveId: string; objectiveTitle: string; organizationId: string; ownerType: string; ownerId: string }) =>
      apiClient.post('/api/v1/approvals/okr', data),
    requestBoApproval: (data: { boId: string; boTitle: string; organizationId: string }) =>
      apiClient.post('/api/v1/approvals/business-opportunity', data),
    listPending: (organizationId: string, params?: { page?: number; size?: number }) =>
      apiClient.get('/api/v1/approvals/pending', { params: { organizationId, ...params } }),
    countPending: (organizationId: string) =>
      apiClient.get('/api/v1/approvals/pending/count', { params: { organizationId } }),
    approve: (id: string) => apiClient.post(`/api/v1/approvals/${id}/approve`),
    reject: (id: string, data?: { reason?: string }) =>
      apiClient.post(`/api/v1/approvals/${id}/reject`, data),
    getApprovalSettings: (orgId: string) =>
      apiClient.get(`/api/v1/organizations/${orgId}/approval-settings`),
    updateApprovalSettings: (orgId: string, data: { requireOkrApproval: boolean; requireBoApproval: boolean }) =>
      apiClient.put(`/api/v1/organizations/${orgId}/approval-settings`, data),
    listDependencyConflicts: (params: {
      organizationId: string
      entityType: string
      entityId: string
      includeResolved?: boolean
    }) => apiClient.get('/api/v1/approvals/dependency-conflicts', { params }),
    resolveConflict: (id: string, data?: { note?: string }) =>
      apiClient.post(`/api/v1/approvals/${id}/resolve-conflict`, data),
  },

  setupConfig: {
    get: (orgId: string) =>
      apiClient.get(`/api/v1/organizations/${orgId}/setup-config`),
    update: (orgId: string, data: Partial<import('../types').SetupConfig>) =>
      apiClient.put(`/api/v1/organizations/${orgId}/setup-config`, data),
  },

  /**
   * What a unit may align its goals to, grouped so the standard path is first.
   *
   * Read before the save rather than validated after it: an upward skip is
   * allowed, so the user needs to see what it means while choosing, not be
   * stopped once they have chosen.
   */
  alignment: {
    options: (unitId: string, params?: { periodId?: string; locale?: string; rungId?: string }) =>
      apiClient.get('/api/v1/alignment/options', { params: { unitId, ...params } }),
    /** The objectives beneath a shared objective's owner that may link to it. */
    linkable: (objectiveId: string) => apiClient.get('/api/v1/alignment/linkable', { params: { objectiveId } }),
  },

  terminology: {
    get: (orgId: string, locale?: string) =>
      apiClient.get(`/api/v1/organizations/${orgId}/terminology`, { params: locale ? { locale } : undefined }),
    update: (
      orgId: string,
      data: { overrides: import('../types').TermOverride[]; locale?: string },
    ) => apiClient.put(`/api/v1/organizations/${orgId}/terminology`, data),
  },

  checkIns: {
    getCurrentWeek: (teamId: string, periodId: string) =>
      apiClient.get(`/api/v1/teams/${teamId}/check-ins/current-week`, { params: { periodId } }),
    list: (teamId: string, periodId: string) =>
      apiClient.get(`/api/v1/teams/${teamId}/check-ins`, { params: { periodId } }),
    previousWeek: (teamId: string, periodId: string) =>
      apiClient.get<{ weekStart: string | null }>(`/api/v1/teams/${teamId}/check-ins/previous-week`, { params: { periodId } }),
    get: (teamId: string, checkInId: string) =>
      apiClient.get(`/api/v1/teams/${teamId}/check-ins/${checkInId}`),
    submit: (teamId: string, data: import('../types').SubmitCheckInRequest) =>
      apiClient.post(`/api/v1/teams/${teamId}/check-ins`, data),
    update: (teamId: string, checkInId: string, data: Partial<import('../types').SubmitCheckInRequest>) =>
      apiClient.put(`/api/v1/teams/${teamId}/check-ins/${checkInId}`, data),
    getStatus: (teamId: string, teamIds: string[]) =>
      apiClient.get(`/api/v1/teams/${teamId}/check-ins/status`, { params: { teamIds: teamIds.join(',') } }),
  },

  auditLog: {
    entityHistory: (entityType: string, entityId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/audit-logs/entity/${entityType}/${entityId}`, { params }),
    userActivity: (userId: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/audit-logs/user/${userId}`, { params }),
    actionHistory: (entityType: string, action: string, params?: { page?: number; size?: number }) =>
      apiClient.get(`/api/v1/audit-logs/actions/${entityType}/${action}`, { params }),
  },

  /** A user's own agent credentials (Preferences -> Agent access). */
  personalAccessTokens: {
    list: () => apiClient.get('/api/v1/users/me/tokens'),
    create: (data: {
      name: string
      organizationId: string
      scopes?: string[]
      expiresInDays?: number
      restrictedEntityType?: string
      restrictedEntityId?: string
    }) => apiClient.post('/api/v1/users/me/tokens', data),
    revoke: (id: string) => apiClient.delete(`/api/v1/users/me/tokens/${id}`),
  },

  /** What an agent did for the user, and taking it back. */
  agentActivity: {
    sessions: () => apiClient.get('/api/v1/users/me/agent-activity'),
    undoSession: (sessionId: string) =>
      apiClient.post(`/api/v1/users/me/agent-activity/sessions/${sessionId}/undo`),
    undoChange: (changeId: string) =>
      apiClient.post(`/api/v1/users/me/agent-activity/changes/${changeId}/undo`),
  },

  apiClients: {
    list: (organizationId: string) =>
      apiClient.get('/api/v1/api-clients', { params: { organizationId } }),
    create: (data: { name: string; organizationId: string; scopes?: string; rateLimit?: number }) =>
      apiClient.post('/api/v1/api-clients', data),
    revoke: (id: string) =>
      apiClient.post(`/api/v1/api-clients/${id}/revoke`),
    rotate: (id: string) =>
      apiClient.post(`/api/v1/api-clients/${id}/rotate`),
  },
}
